import React from "react";
import styled from "styled-components";
import { Col, Row } from "reactstrap";
import { RebelColors, RebelDangerButton } from "../../../styles/rebel-experiences";
import { displayValue } from "../helper";

/**
 * Property tab showing cost breakdown using basicViewPricing data.
 * basicViewPricing is a map of category-id -> per-person price.
 * Total per category = price * attendees (and * tripLength for applicable day-based categories).
 */
const RebelBudgetPropertyTab = ({ villaId, villa, categories, config, tripLength, currencySymbol, onRemove }) => {

    if (!villa) {
        return <LoadingText>Loading property data...</LoadingText>;
    }

    const pricing = villa.basicViewPricing || {};
    const attendees = config.attendees || 1;

    // Get category name from id
    const getCategoryName = (catId) => {
        const cat = categories.find(c => c.id === catId || c._docId === catId);
        return cat?.name || cat?.title || catId;
    };

    // Determine if a category should be multiplied by trip length
    // Categories that are typically "per day": accommodation, meals
    const isDayBased = (catId, catName) => {
        const name = (catName || catId || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging") ||
               name.includes("meal") || name.includes("food") || name.includes("beverage") ||
               name.includes("breakfast") || name.includes("lunch") || name.includes("dinner");
    };

    // Build cost rows from basicViewPricing
    const costRows = Object.entries(pricing)
        .filter(([, price]) => parseFloat(price) > 0)
        .map(([catId, price]) => {
            const perPerson = parseFloat(price) || 0;
            const catName = getCategoryName(catId);
            const dayBased = isDayBased(catId, catName);
            const nights = dayBased ? (tripLength || 1) : 1;
            const totalPerPerson = perPerson * nights;
            const totalForTrip = totalPerPerson * attendees;
            return { catId, catName, perPerson, nights, dayBased, totalPerPerson, totalForTrip };
        });

    const subtotalPerPerson = costRows.reduce((sum, row) => sum + row.totalPerPerson, 0);
    const subtotalTrip = costRows.reduce((sum, row) => sum + row.totalForTrip, 0);

    return (
        <div>
            {/* Property header */}
            <PropertyHeader>
                <PropertyName>{villa.villaName || villa.name || "Property"}</PropertyName>
                {villa.location && <PropertyLocation>{villa.location}</PropertyLocation>}
            </PropertyHeader>

            {/* Villa details */}
            {(villa.numberOfBedrooms || villa.distanceFromAirport || villa.nearestCity || villa.distanceToCity || villa.villaURL) && (
                <VillaDetailsContainer>
                    {villa.numberOfBedrooms > 0 && <VillaDetail><strong>Bedrooms:</strong> {villa.numberOfBedrooms}</VillaDetail>}
                    {villa.distanceFromAirport && <VillaDetail><strong>Distance from Airport:</strong> {villa.distanceFromAirport}</VillaDetail>}
                    {villa.nearestCity && <VillaDetail><strong>Nearest City:</strong> {villa.nearestCity}</VillaDetail>}
                    {villa.distanceToCity && <VillaDetail><strong>Distance to City:</strong> {villa.distanceToCity}</VillaDetail>}
                    {villa.villaURL && (
                        <ViewPropertyButton
                            href={villa.villaURL.startsWith('http') ? villa.villaURL : `https://${villa.villaURL}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Property
                        </ViewPropertyButton>
                    )}
                </VillaDetailsContainer>
            )}

            {/* Cost breakdown table */}
            <CostTableContainer>
                {/* Header */}
                <HeaderRow>
                    <Col>
                        <BoldLabel>Category</BoldLabel>
                    </Col>
                    <Col lg="2">
                        <BoldLabel>Per Person ({currencySymbol})</BoldLabel>
                    </Col>
                    <Col lg="2">
                        <BoldLabel>Total ({currencySymbol})</BoldLabel>
                    </Col>
                </HeaderRow>

                {/* Cost rows */}
                {costRows.map(row => (
                    <CostRow key={row.catId}>
                        <Col>
                            <CostText>
                                {row.catName}
                                {row.dayBased && tripLength > 0 && (
                                    <DayNote> ({row.nights} night{row.nights !== 1 ? 's' : ''})</DayNote>
                                )}
                            </CostText>
                        </Col>
                        <Col lg="2">
                            <CostText>{currencySymbol}{displayValue(row.totalPerPerson)}</CostText>
                        </Col>
                        <Col lg="2">
                            <CostText>{currencySymbol}{displayValue(row.totalForTrip)}</CostText>
                        </Col>
                    </CostRow>
                ))}

                {costRows.length === 0 && (
                    <EmptyRow>
                        <Col>
                            <EmptyText>No pricing data available for this property.</EmptyText>
                        </Col>
                    </EmptyRow>
                )}

                {/* Separator */}
                {costRows.length > 0 && (
                    <>
                        <SeparatorRow>
                            <Col><SeparatorLine /></Col>
                        </SeparatorRow>

                        {/* Subtotal */}
                        <CostRow>
                            <Col>
                                <BoldLabel>Estimated Total</BoldLabel>
                            </Col>
                            <Col lg="2">
                                <BoldGoldLabel>{currencySymbol}{displayValue(subtotalPerPerson)}</BoldGoldLabel>
                            </Col>
                            <Col lg="2">
                                <BoldGoldLabel>{currencySymbol}{displayValue(subtotalTrip)}</BoldGoldLabel>
                            </Col>
                        </CostRow>
                    </>
                )}
            </CostTableContainer>

            {/* Remove property button */}
            <RemoveRow>
                <RebelDangerButton onClick={onRemove}>
                    Remove Property
                </RebelDangerButton>
            </RemoveRow>
        </div>
    );
};

export default RebelBudgetPropertyTab;

// Calculate total cost for a villa (used by summary)
export const calculateVillaTotalPerPerson = (villa, categories, tripLength) => {
    if (!villa || !villa.basicViewPricing) return 0;

    const pricing = villa.basicViewPricing;

    const getCategoryName = (catId) => {
        const cat = categories.find(c => c.id === catId || c._docId === catId);
        return cat?.name || cat?.title || catId;
    };

    const isDayBased = (catId, catName) => {
        const name = (catName || catId || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging") ||
               name.includes("meal") || name.includes("food") || name.includes("beverage") ||
               name.includes("breakfast") || name.includes("lunch") || name.includes("dinner");
    };

    let total = 0;
    Object.entries(pricing).forEach(([catId, price]) => {
        const perPerson = parseFloat(price) || 0;
        if (perPerson > 0) {
            const catName = getCategoryName(catId);
            const nights = isDayBased(catId, catName) ? (tripLength || 1) : 1;
            total += perPerson * nights;
        }
    });

    return total;
};

const LoadingText = styled.p`
    color: ${RebelColors.textMuted};
    text-align: center;
    padding: 2rem;
`;

const PropertyHeader = styled.div`
    margin-bottom: 1rem;
`;

const PropertyName = styled.h3`
    color: ${RebelColors.gold};
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
`;

const PropertyLocation = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.9rem;
    margin-bottom: 0;
`;

const VillaDetailsContainer = styled.div`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 12px;
    padding: 20px;
    margin: 16px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2);
`;

const VillaDetail = styled.div`
    color: ${RebelColors.text};
    font-size: 14px;
    padding: 6px 14px;
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.1) 0%, rgba(253, 181, 6, 0.05) 50%, transparent 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 6px;

    strong {
        color: ${RebelColors.gold};
        margin-right: 4px;
    }
`;

const ViewPropertyButton = styled.a`
    display: inline-flex;
    align-items: center;
    padding: 8px 20px;
    background: linear-gradient(90deg, #fdb506 0%, #f59e0b 100%);
    border: 2px solid #fdb506;
    border-radius: 6px;
    color: #000000;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(253, 181, 6, 0.4);
        color: #000000;
        text-decoration: none;
    }
`;

const CostTableContainer = styled.div`
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(31, 41, 55, 0.85) 50%, rgba(55, 65, 81, 0.85) 100%);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 12px;
    padding: 20px;
    margin-top: 1rem;
`;

const HeaderRow = styled(Row)`
    border-bottom: 2px solid rgba(253, 181, 6, 0.4);
    padding-bottom: 8px;
    margin-bottom: 8px;
`;

const CostRow = styled(Row)`
    padding: 6px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.1);
    display: flex;
    align-items: center;

    &:last-child {
        border-bottom: none;
    }
`;

const SeparatorRow = styled(Row)`
    padding: 0;
`;

const SeparatorLine = styled.div`
    background-color: ${RebelColors.gold};
    height: 2px;
    margin: 8px 0;
    border-radius: 1px;
`;

const EmptyRow = styled(Row)`
    padding: 1rem 0;
`;

const BoldLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    margin-bottom: 0;
    font-size: 0.9rem;
`;

const BoldGoldLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 700;
    margin-bottom: 0;
    font-size: 1rem;
`;

const CostText = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-size: 0.9rem;
`;

const DayNote = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
`;

const EmptyText = styled.p`
    color: ${RebelColors.textMuted};
    text-align: center;
    font-style: italic;
    margin: 0;
`;

const RemoveRow = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
`;
